import React, { useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  IconButton,
  Paper,
  Typography,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  Edit as EditIcon,
  Work as WorkIcon,
  Business as BusinessIcon
} from '@mui/icons-material';

const UserProfile = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // This would come from your application state in a real app
  const [user, setUser] = useState({
    firstName: 'Jane',
    lastName: 'Doe',
    username: 'janedoe',
    email: 'jane@example.com',
    phone: '+1234567890',
    jobTitle: 'Marketing Manager',
    linkedin: 'https://linkedin.com/in/janedoe',
    twitter: 'https://twitter.com/janedoe',
    profilePicture: 'https://i.pravatar.cc/150?img=3',
    role: 'subscriber',
    organization: 'VPMA Org',
  });

  
  const handleEdit = () => {
    // Handle edit functionality
    console.log('Edit profile clicked');
  };

  return (
    <Box sx={{ py: 4 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          borderRadius: 2, 
          overflow: 'hidden',
          background: theme.palette.background.paper
        }}
      >
        {/* Header/Banner Section */}
        <Box 
          sx={{ 
            height: '120px', 
            bgcolor: theme.palette.primary.main,
            position: 'relative'
          }}
        />
        
        {/* Profile Content */}
        <Box sx={{ p: 3, pt: 0 }}>
          <Grid container spacing={3}>
            {/* Left Column - Avatar and Basic Info */}
            <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'center' : 'flex-start' }}>
              <Avatar
                src={user.profilePicture}
                alt={`${user.firstName} ${user.lastName}`}
                sx={{
                  width: 120,
                  height: 120,
                  border: '4px solid white',
                  mt: '-60px',
                  boxShadow: theme.shadows[3]
                }}
              />
              
              <Typography variant="h5" sx={{ mt: 2, fontWeight: 'bold' }}>
                {user.firstName} {user.lastName}
              </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                @{user.username}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <WorkIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                <Typography variant="body2">{user.jobTitle}</Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BusinessIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                <Typography variant="body2">{user.organization}</Typography>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <IconButton 
                  color="primary" 
                  component="a" 
                  href={user.linkedin} 
                  target="_blank"
                  size="small"
                  sx={{ bgcolor: 'action.hover' }}
                >
                  <LinkedInIcon fontSize="small" />
                </IconButton>
                <IconButton 
                  color="primary" 
                  component="a" 
                  href={user.twitter} 
                  target="_blank"
                  size="small"
                  sx={{ bgcolor: 'action.hover' }}
                >
                  <TwitterIcon fontSize="small" />
                </IconButton>
              </Box>
              
              <Button 
                variant="outlined" 
                color="primary" 
                startIcon={<EditIcon />}
                onClick={handleEdit}
                sx={{ mt: 'auto' }}
              >
                Edit Profile
              </Button>
            </Grid>
            
            {/* Right Column - Detailed Information */}
            <Grid item xs={12} md={8}>
              <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Contact Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <EmailIcon color="action" sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Email
                          </Typography>
                          <Typography variant="body1">
                            {user.email}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PhoneIcon color="action" sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Phone
                          </Typography>
                          <Typography variant="body1">
                            {user.phone}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
              
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Account Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Username
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {user.username}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Role
                      </Typography>
                      <Typography variant="body1" gutterBottom sx={{ textTransform: 'capitalize' }}>
                        {user.role}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Organization
                      </Typography>
                      <Typography variant="body1">
                        {user.organization}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default UserProfile;